/**
 * What each guide is called and the one line it says, by key — for a guide shown for a
 * button under the pointer or a tool picked for the first time, when the tool in hand
 * (and its rail) is not the one being described. The Control guide for the tool in hand
 * reads its rail instead (`toolFeedback`), which says the same things.
 */
export const GUIDE_TEXT: Record<string, readonly [name: string, line: string]> = {
  select: ['Select', 'Click a shape to choose it; drag it to move it.'],
  'select:point': ['Direct', 'Choose the points inside a shape.'],
  'select:area': ['Marquee', 'Drag a box round points.'],
  'select:lasso': ['Lasso', 'Draw a loop round points.'],
  rect: ['Rectangle', 'Drag from one corner to the other.'],
  ellipse: ['Ellipse', 'Drag to draw an ellipse. Hold Shift for a circle.'],
  polygon: ['Polygon', 'Drag to draw a polygon.'],
  star: ['Star', 'Drag to draw a star.'],
  line: ['Line', 'Drag between two points.'],
  arc: ['Arc', 'Drag to draw an arc.'],
  text: ['Point type', 'Click, then type where you clicked.'],
  'text:area': ['Area type', 'Drag a box and type inside it.'],
  'text:path': ['Type on a path', 'Press a path and the words ride it.'],
  pen: ['Pen', 'Click to add points. Drag to curve the path.'],
  'pen:add': ['Add point', 'Click a path edge to insert a point.'],
  'pen:remove': ['Remove point', 'Click a point to remove it.'],
  'pen:convert': ['Convert point', 'Click a point to switch between corner and smooth.'],
  draw: ['Draw', 'Draw a rough shape, then hold to straighten it.'],
  'draw:shape': ['Shape assist', 'Draw a rough shape, then hold to straighten it.'],
  'draw:pencil': ['Pencil', 'Draw a freehand path.'],
  'draw:blob': ['Fill brush', 'Paint filled shapes that merge as they touch.'],
  'draw:smooth': ['Smooth', 'Brush over a path to soften its curves.'],
  'draw:erase': ['Erase', 'Brush over a path to remove sections.'],
  'draw:join': ['Join', 'Click between two open ends to connect them.'],
  liquify: ['Liquify', 'Drag over an outline to push it.'],
  'liquify:warp': ['Warp', 'Drag over an outline to push it.'],
  'liquify:twirl': ['Twirl', 'Hold over an outline to twist it round the brush.'],
  'liquify:pucker': ['Pinch', 'Pull the outline toward the brush centre.'],
  'liquify:bloat': ['Swell', 'Push the outline away from the brush centre.'],
  'liquify:scallop': ['Scallop', 'Add rounded notches along an outline.'],
  'liquify:crystallize': ['Crystallize', 'Add outward spikes along an outline.'],
  'liquify:wrinkle': ['Wrinkle', 'Add small, uneven folds along an outline.'],
  width: ['Width', 'Drag away from a path to thicken its stroke.'],
  cut: ['Cut', 'Rub a shape away wherever the brush passes.'],
  'cut:erase': ['Eraser', 'Rub a shape away wherever the brush passes.'],
  'cut:scissors': ['Scissors', 'Press on an edge to cut the path there.'],
  'cut:knife': ['Knife', 'Draw across a shape to cut it into pieces.'],
  warp: ['Transform', 'Drag a corner to resize.'],
  'warp:transform': ['Scale', 'Drag a corner to resize.'],
  'warp:perspective': ['Perspective', 'Drag a corner; its partner moves the other way.'],
  'warp:distort': ['Distort', 'Drag each corner on its own.'],
  'warp:puppet': ['Pin warp', 'Add pins, then drag one to bend the shape.'],
  slice: ['Slice', 'Drag out part of the page to export on its own.'],
  'slice:choose': ['Select slice', 'Choose a slice to move, size or delete.'],
  measure: ['Measure', 'Drag between two places for the distance.'],
  'measure:angular': ['Angle', 'Drag one arm, then click where the other goes.'],
  'measure:radial': ['Radius', 'Click a round shape for its radius.'],
  builder: ['Shape builder', 'Loop round pieces, then merge them.'],
  blend: ['Blend', 'Click one shape, then another, for the steps between.'],
  cloner: ['Cloner', 'Make an array of copies of what is chosen.'],
  // The effector recipes, offered on a cloner's strip: a picture of what each does to an array.
  'effector:scatter': ['Scatter', 'Every copy knocked a little off its place, each its own way.'],
  'effector:grow': ['Grow', 'Each copy a little bigger than the one before it.'],
  'effector:twist': ['Twist', 'Each copy turned a little further than the one before it.'],
  'effector:fade': ['Fade', 'Each copy a little lighter than the one before it.'],
  'effector:magnify': ['Magnify', 'The copies in the middle swell; the ones at the edge stay as they were.'],
  artboard: ['Artboard', 'Drag to draw an artboard, or click to place a preset.'],
  addon: ['Add-on', 'Run an add-on on what is chosen.'],
};

export function guideText(key: string): readonly [string, string] | null {
  return GUIDE_TEXT[key] ?? GUIDE_TEXT[key.split(':')[0]!] ?? null;
}
